import genericEmoji from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import InsertEmoticon from "@mui/icons-material/InsertEmoticon";
import { CircularProgress, Popover, ToggleButton, Tooltip, useTheme } from "@mui/material";
import { $getPreviousSelection, COMMAND_PRIORITY_EDITOR, type LexicalEditor } from "lexical";
import { type FC, type MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSupabase } from "../../../../../server/supabase/hooks";
import { isChatChannel } from "../../../../channels/types";
import {
  type CustomEmoji,
  SupabaseCustomEmojiService,
} from "../../../../slack/server/slack-supabase-service";
import { useLexicalContext } from "../../lexical-context";
import { $createCustomEmojiNode } from "../../nodes/custom-emoji/custom-emoji-node";
import { $createEmojiNode } from "../../nodes/emoji-node";
import { INSERT_EMOJI_COMMAND } from "../../types/commands";
import type { Emoji } from "../../types/emoji";
import type { ToolbarProps } from "./props";

export const EmojiPicker: FC<ToolbarProps> = ({ disabled }) => {
  const { t } = useTranslation();
  // Hoist loaded emojis so they can be rused if the popup is opened twice
  const [customEmojis, setCustomEmojis] = useState<CustomEmoji[]>();
  const [editor] = useLexicalComposerContext();
  const [displayPicker, setDisplayPicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  useEffect(
    () =>
      editor.registerCommand(
        INSERT_EMOJI_COMMAND,
        ({ id, native }) => {
          const selection = $getPreviousSelection();
          const node = native ? $createEmojiNode(id, native) : $createCustomEmojiNode(id);

          selection?.insertNodes([node]);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    [editor],
  );

  const toggleEmojiPicker = useCallback((event: MouseEvent<Element>): void => {
    event.preventDefault();
    setDisplayPicker((display) => !display);
    setAnchorEl(event.currentTarget);
  }, []);

  return (
    <>
      <Tooltip title={t("Emoji")}>
        <span>
          <ToggleButton
            data-analytics-id="lexical-toolbar-emoji"
            data-testid="add-emoji"
            onClick={toggleEmojiPicker}
            selected={displayPicker}
            value="emoji"
            disabled={disabled}
            sx={{ border: "none", "&.Mui-disabled": { border: "none" } }}
          >
            <InsertEmoticon data-testid="InsertEmoticonIcon" fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>

      <Popover
        data-testid="emoji-picker"
        disableAutoFocus={true}
        open={displayPicker}
        anchorEl={anchorEl}
        onClose={() => {
          setDisplayPicker(false);
          setAnchorEl(null);
        }}
      >
        <PickerInner
          editor={editor}
          customEmojis={customEmojis}
          setCustomEmojis={setCustomEmojis}
          onSelection={() => {
            setDisplayPicker(false);
          }}
        />
      </Popover>
    </>
  );
};

const PickerInner: FC<{
  editor: LexicalEditor;
  customEmojis: CustomEmoji[] | undefined;
  setCustomEmojis: (d: CustomEmoji[]) => void;
  onSelection: () => void;
}> = ({ editor, customEmojis, setCustomEmojis, onSelection }) => {
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const theme = useTheme();
  const { channelName } = useLexicalContext();
  // Lazily load custom emojis on open as this picker isn't often used and loading all emojis is extreemly slow on large accounts
  useSupabase(
    async ({ supabase, account_id }) => {
      if (customEmojis || !isChatChannel(channelName)) {
        setCustomEmojis(customEmojis ?? []);
        setLoading(false);
        return;
      }
      const { data } = await new SupabaseCustomEmojiService(supabase).getAll(account_id, {
        order: [{ column: "emoji" }],
        filter: [{ key: "channel", operator: "eq", value: channelName }],
      });

      setLoading(false);

      setCustomEmojis(data ?? []);
    },
    // Technically the channelName change dosn't trigger load; but we don't reuse the button anyway
    [channelName, customEmojis, setCustomEmojis],
  );

  const onSelectOption = useCallback(
    (selected: Emoji) => {
      editor.dispatchCommand(INSERT_EMOJI_COMMAND, selected);
      onSelection();
    },
    [editor, onSelection],
  );
  const custom = useMemo(() => {
    if (!customEmojis) return [];

    return [
      {
        id: "custom",
        name: t("Custom"),
        emojis: customEmojis.map((emoji) => ({
          id: emoji.emoji,
          name: emoji.emoji,
          keywords: [emoji.emoji],
          skins: [{ src: emoji.url }],
        })),
      },
    ];
  }, [customEmojis, t]);

  if (loading) return <CircularProgress />;
  return (
    <Picker
      custom={custom}
      theme={theme.palette.mode}
      data={genericEmoji}
      onEmojiSelect={onSelectOption}
      emoji=""
      title=""
      exclude={["recent"]}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={true}
    />
  );
};
